import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import Section from '@/components/Section'
import ColorPicker from '@/components/widgets/ColorPicker'
import { usePageTitle } from '@/components/usePageTitle'
import { ApiError, createCheckout, lookup } from '@/lib/api'
import { hexContrast } from '@/lib/color'
import { randomColorName } from '@/lib/names'
import { PLANS, formatPrice, validateColorName, validateRegistration, type PlanId, type RegistrationErrors } from '@/lib/register'
import type { RegistryColor } from '@/lib/registry'

type Availability = 'checking' | 'available' | 'taken'

/**
 * The register flow: pick a color,
 * name it, choose how long to keep it, and continue to checkout. Availability
 * is checked live against the registry; /api/checkout checks it again.
 */
export default function Register() {
  usePageTitle('Register', 'Register your favorite color with ColorMark.')
  const [params] = useSearchParams()
  const navigate = useNavigate()

  const [hex, setHex] = useState('')
  const [hexMatch, setHexMatch] = useState<RegistryColor | null | undefined>(undefined)
  const onPick = useCallback((h: string, match: RegistryColor | null | undefined) => {
    setHex(h)
    setHexMatch(match)
  }, [])

  const [name, setName] = useState('')
  const [plan, setPlan] = useState<PlanId>('1')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState<RegistrationErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  // Name availability: the slug is looked up (debounced) once the name is valid.
  const nameCheck = validateColorName(name)
  const slug = nameCheck.ok ? nameCheck.slug : ''
  const [nameLooked, setNameLooked] = useState<{ slug: string; taken: boolean } | null>(null)
  useEffect(() => {
    if (!slug) return
    let alive = true
    const t = setTimeout(() => {
      lookup(slug).then((c) => {
        if (alive) setNameLooked({ slug, taken: c !== null })
      })
    }, 250)
    return () => {
      alive = false
      clearTimeout(t)
    }
  }, [slug])
  const nameAvailability: Availability | null = !slug ? null : nameLooked?.slug !== slug ? 'checking' : nameLooked.taken ? 'taken' : 'available'
  const hexAvailability: Availability | null = !hex ? null : hexMatch === undefined ? 'checking' : hexMatch ? 'taken' : 'available'

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setFailure(null)
    const v = validateRegistration({ hex, name, plan, firstName, lastName, email })
    const next: RegistrationErrors = v.ok ? {} : { ...v.errors }
    if (hexAvailability === 'taken') next.hex = 'Sorry, this Color Code is not available.'
    if (nameAvailability === 'taken') next.name = 'Sorry, this Color Name is not available.'
    setErrors(next)
    if (!v.ok || Object.keys(next).length) return

    setSubmitting(true)
    try {
      const r = await createCheckout({ hex: v.value.hex, name: v.value.name, plan: v.value.plan.id, firstName, lastName, email })
      navigate(r.url)
    } catch (err) {
      setSubmitting(false)
      if (err instanceof ApiError && Object.keys(err.errors).length) setErrors(err.errors as RegistrationErrors)
      else setFailure('Sorry, registration is not available right now. Please try again in a moment.')
    }
  }

  const fg = hex ? (hexContrast(hex) === 'black' ? '#111111' : '#ffffff') : '#111111'

  return (
    <Section className="pt-40 md:pt-52">
      <div className="mx-auto grid max-w-[1800px] grid-cols-1 items-start gap-12 lg:grid-cols-2 lg:gap-16">
        <div>
          <h1>Register your favorite color.</h1>
          <p className="mt-8 max-w-3xl">
            Pick a color, give it a name, and choose how long to keep it. Your color gets its own page, a certificate of
            registration, and a listing in the <Link to="/registry">registry</Link>.
          </p>

          <form onSubmit={onSubmit} className="mt-10 max-w-2xl space-y-8 font-body" noValidate>
            <div>
              <label htmlFor="colorName" className="block text-[1.05em]">
                Color Name
              </label>
              <input
                id="colorName"
                name="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name Your Color"
                maxLength={60}
                autoComplete="off"
                aria-invalid={errors.name ? true : undefined}
                aria-describedby="colorNameHelp"
                className="field-boxed mt-3 text-lg"
              />
              <p className="mt-2 text-right text-[13px] italic">
                <button
                  type="button"
                  onClick={() => {
                    setName(randomColorName(hex || '#808080'))
                    setErrors((e) => ({ ...e, name: undefined }))
                  }}
                  className="text-[#007bff] hover:underline"
                >
                  Can't think of a name? RANDOMIZE!
                </button>
              </p>
              <p id="colorNameHelp" className="min-h-6 text-sm" aria-live="polite">
                {errors.name ? (
                  <span className="text-red-700">{errors.name}</span>
                ) : !name ? (
                  <span className="text-neutral-500">Names must be at least 6 letters with no special characters.</span>
                ) : !nameCheck.ok ? (
                  <span className="text-neutral-500">{nameCheck.error}</span>
                ) : nameAvailability === 'taken' ? (
                  <span className="text-red-700">Sorry, this Color Name is not available.</span>
                ) : nameAvailability === 'available' ? (
                  <span className="text-green-700">Name Available!</span>
                ) : (
                  <span className="text-neutral-500">Checking…</span>
                )}
              </p>
            </div>

            <div>
              <span className="block text-[1.05em]">Color Code</span>
              <div className="mt-3 flex items-center gap-3 border border-neutral-300 px-4 py-3 text-lg" style={{ backgroundColor: hex || '#ffffff', color: fg }}>
                <span className="font-medium tracking-wide">{hex || '#'}</span>
                <span className="text-sm opacity-80">
                  {hexAvailability === 'available' && 'Color Available!'}
                  {hexAvailability === 'taken' && hexMatch && `Already registered as ${hexMatch.name}`}
                  {hexAvailability === 'checking' && 'Checking…'}
                </span>
              </div>
              <p className="mt-2 min-h-6 text-sm" aria-live="polite">
                {errors.hex ? <span className="text-red-700">{errors.hex}</span> : <span className="text-neutral-500">Use the picker to choose your color.</span>}
              </p>
            </div>

            <fieldset>
              <legend className="text-[1.05em]">Registration Type</legend>
              <div className="mt-3 space-y-2">
                {PLANS.map((p) => (
                  <label key={p.id} className="flex cursor-pointer items-center gap-3 py-1 text-[1.1em]">
                    <input
                      type="radio"
                      name="plan"
                      value={p.id}
                      checked={plan === p.id}
                      onChange={() => setPlan(p.id)}
                      className="h-5 w-5 cursor-pointer accent-black"
                    />
                    <span>
                      <span className="font-medium">{p.label}:</span> {formatPrice(p.priceCents)}
                      {p.note && <span className="italic"> ({p.note})</span>}
                    </span>
                  </label>
                ))}
              </div>
              <p className="mt-2 text-sm text-neutral-500">Registration does not automatically renew.</p>
            </fieldset>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="firstName" className="block text-[1.05em]">
                  First Name
                </label>
                <input id="firstName" name="firstName" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" aria-invalid={errors.firstName ? true : undefined} className="field-boxed mt-3" />
                <p className="mt-2 min-h-6 text-sm text-red-700">{errors.firstName}</p>
              </div>
              <div>
                <label htmlFor="lastName" className="block text-[1.05em]">
                  Last Name
                </label>
                <input id="lastName" name="lastName" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" aria-invalid={errors.lastName ? true : undefined} className="field-boxed mt-3" />
                <p className="mt-2 min-h-6 text-sm text-red-700">{errors.lastName}</p>
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-[1.05em]">
                Email
              </label>
              <input id="email" name="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" aria-invalid={errors.email ? true : undefined} className="field-boxed mt-3" />
              <p className="mt-2 min-h-6 text-sm text-red-700">{errors.email}</p>
            </div>

            <div>
              <button type="submit" className="btn btn-black" disabled={submitting}>
                {submitting ? 'One moment…' : 'Continue to Checkout'}
              </button>
              <p className="mt-4 min-h-6 text-sm text-red-700" role="alert">
                {failure}
              </p>
            </div>
          </form>
        </div>

        <div className="flex justify-center lg:justify-end">
          <ColorPicker initialHex={params.get('hex') ?? undefined} onChange={onPick} linkToRegister={false} />
        </div>
      </div>
    </Section>
  )
}
